import React, { useCallback, useState } from 'react';
import { Stack, Alert, Tooltip, MenuItem, IconButton, CircularProgress } from '@mui/material';
import Footer from '../general/Footer';
import { Close, History } from '@mui/icons-material';
import { Button, Typography } from 'components/index';
import SaveIcon from '@mui/icons-material/Save';
import { KeyboardArrowUp } from '@mui/icons-material';
import { StyledMenu } from 'common/utils/common.util';
import { PermissionGate } from '../common/PermissionGate';
import { ROUTES } from 'common/constants/urls';
import { CustomTooltip } from '../common/CustomTooltip';
import { retrieveProjectDraws } from './project.api';
import { NO_PAYAPPS_UPLOADED_TOOLTIP, START_NEXT_REPORT_TOOLTIP } from './project.util';
import { handleError } from '../../common/utils/util.errors';
import UploadDialog from '../file-upload/UploadDialog';
import { DrawOption } from '../file-upload/PayappUploadDialog';
import { useMagicReportDrawSelection } from '../../hooks/useMagicReportDrawSelection';
import MagicReportDrawSelectDialog from './MagicReportDrawSelectDialog';
import RequiredFieldsGateDialog from './RequiredFieldsGateDialog';
import { usePrompt } from '../AppPrompt';

interface FooterProps {
  projectId: string;
  text: string;
  deleting: boolean;
  onDelete: () => void;
  isGeneratingReport: boolean;
  isExporting: boolean;
  onGenerateReport: () => void;
  isSaving: boolean;
  isEditMode: boolean;
  onSave: () => void;
  onCancel: () => void;
  isProjectDirty: boolean;
  exportProject: () => void;
  onOpenExistingLimitedReport?: () => void;
  onCreateNewLimitedReport?: () => void;
  onOpenExistingMagicReport?: () => void;
  onCreateNewMagicReport?: () => void;
  monitoringType?: string;
  limitedReportId?: string;
  magicReportId?: string;
  magicReportAwaitingPayapp?: boolean;
  onNextMagicReportStarted?: (reportId: string) => void;
  onAddDraw?: () => void;
  onMagicReportDrawsHidden?: (drawIds: string[]) => void;
}

const onSeePastVersions = (projectId: string) => {
  window.open(`${ROUTES.PROJECT_VERSIONS}/${projectId}`, '_blank');
};

const ProjectFooter: React.FC<FooterProps> = ({
  projectId,
  text,
  deleting,
  onDelete,
  isGeneratingReport,
  onGenerateReport,
  isExporting,
  isSaving,
  isEditMode,
  onSave,
  onCancel,
  isProjectDirty,
  exportProject,
  onOpenExistingLimitedReport,
  onCreateNewLimitedReport,
  onOpenExistingMagicReport,
  onCreateNewMagicReport,
  monitoringType,
  limitedReportId,
  magicReportId,
  magicReportAwaitingPayapp = false,
  onNextMagicReportStarted,
  onAddDraw,
  onMagicReportDrawsHidden,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const showMagicReport = process.env.REACT_APP_SHOW_MAGIC_REPORT == 'true';
  const open = Boolean(anchorEl);

  const [payappUploadDialogOpen, setPayappUploadDialogOpen] = useState(false);
  const [payappUploadProject, setPayappUploadProject] = useState<{
    id: string;
    projectNumber: string;
    projectName: string;
  } | null>(null);
  const [payappUploadDraws, setPayappUploadDraws] = useState<DrawOption[]>([]);
  const [payappUploadLoading, setPayappUploadLoading] = useState(false);

  const { getConfirmation } = usePrompt();
  const {
    openDrawSelection,
    confirmDrawSelection,
    closeDialog: closeMagicReportDrawSelect,
    dialogOpen: magicReportDrawSelectOpen,
    draws: magicReportDraws,
    project: magicReportProject,
    existingDrawIds: magicReportExistingDrawIds,
    existingHiddenCoLienDrawIds: magicReportExistingHiddenCoLienDrawIds,
    hasExistingReport: hasExistingMagicReport,
    isPreparing: magicReportLoading,
    isOpening: magicReportOpening,
    mode: magicReportMode,
    requiredFieldsGap,
    requiredFieldsGapProjectId,
    requiredFieldsGapProjectSummary,
    requiredFieldsGapOpen,
    retryAfterFieldsFixed,
    closeRequiredFieldsGap,
  } = useMagicReportDrawSelection();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenPayappUpload = useCallback(async () => {
    if (!projectId) return;
    setPayappUploadLoading(true);
    try {
      const response = await retrieveProjectDraws(projectId);
      const draws = (response?.draws || []) as DrawOption[];
      setPayappUploadProject({
        id: projectId,
        projectNumber: response?.projectNumber ?? '',
        projectName: response?.projectTitle ?? '',
      });
      setPayappUploadDraws(draws);
      setPayappUploadDialogOpen(true);
    } catch (error) {
      handleError(error);
    } finally {
      setPayappUploadLoading(false);
    }
  }, [projectId]);

  const handleClosePayappUploadDialog = useCallback(() => {
    setPayappUploadDialogOpen(false);
    setPayappUploadProject(null);
    setPayappUploadDraws([]);
  }, []);

  const handleOpenMagicReport = useCallback(async () => {
    if (!projectId) return;
    await openDrawSelection(projectId, 'open', { onDrawsHidden: onMagicReportDrawsHidden });
  }, [projectId, openDrawSelection, onMagicReportDrawsHidden]);

  const handleStartNextMagicReport = useCallback(async () => {
    if (!projectId) return;
    handleClose();
    await openDrawSelection(projectId, 'next-report', { onNextReportStarted: onNextMagicReportStarted });
  }, [projectId, openDrawSelection, onNextMagicReportStarted]);

  const handleMagicReportDrawConfirm = useCallback(
    async (drawIds: string[], hiddenCoLienDrawIds: string[]) => {
      await confirmDrawSelection(drawIds, hiddenCoLienDrawIds, {
        onConfirmReplace: () =>
          getConfirmation({
            heading: 'Change report draws',
            description:
              'Changing the selected draws will archive the current magic report and start a new one for the newly selected draws. Continue?',
            confirmButton: { label: 'Continue', color: 'error' },
            cancelButton: { label: 'Cancel', color: 'secondary' },
          }),
      });
    },
    [confirmDrawSelection, getConfirmation],
  );

  return (
    <Footer>
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} flex={1} spacing={2}>
        <Typography variant='subtitle2' justifyContent='center'>
          {text}
          <CustomTooltip title='See past versions' placement='top'>
            <IconButton onClick={() => onSeePastVersions(projectId)} color='primary'>
              <History />
            </IconButton>
          </CustomTooltip>
        </Typography>

        <Stack direction={'row'} spacing={1}>
          {isProjectDirty ? <Alert severity='warning'>Project has unsaved changes</Alert> : null}
        </Stack>

        <Stack direction={'row'} spacing={2}>
          {isEditMode || isProjectDirty ? (
            <Button variant='outlined' color='error' onClick={onCancel} startIcon={<Close />} disabled={isSaving}>
              Cancel
            </Button>
          ) : (
            <>
              {/* Left to right in workflow order: Start Next Report, Upload Payapp, Open Magic Report. */}
              {showMagicReport && monitoringType === 'Magic' && onCreateNewMagicReport && (
                <Tooltip title={magicReportAwaitingPayapp ? NO_PAYAPPS_UPLOADED_TOOLTIP : START_NEXT_REPORT_TOOLTIP} arrow={false}>
                  <span>
                    <Button
                      variant='outlined'
                      onClick={handleStartNextMagicReport}
                      disabled={magicReportLoading || magicReportAwaitingPayapp}
                    >
                      Start next report
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Tooltip title='Update G702/3 from last month and upload payapp' arrow={false}>
                <span>
                  <Button
                    variant='outlined'
                    onClick={handleOpenPayappUpload}
                    disabled={payappUploadLoading}
                    startIcon={payappUploadLoading ? <CircularProgress size={20} color='inherit' /> : undefined}
                  >
                    Upload payapp
                  </Button>
                </span>
              </Tooltip>
              {showMagicReport && monitoringType === 'Magic' && (
                <Tooltip title={magicReportAwaitingPayapp ? NO_PAYAPPS_UPLOADED_TOOLTIP : 'Edit the in-progress report'} arrow={false}>
                  <span>
                    <Button
                      variant='outlined'
                      onClick={handleOpenMagicReport}
                      disabled={magicReportLoading || magicReportAwaitingPayapp}
                      startIcon={magicReportLoading ? <CircularProgress size={20} color='inherit' /> : undefined}
                    >
                      Open magic report
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Button startIcon={<KeyboardArrowUp />} variant='outlined' onClick={handleClick}>
                Other Actions
              </Button>
              <StyledMenu
                id='long-menu'
                MenuListProps={{ 'aria-labelledby': 'long-button' }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                  paper: {
                    sx: {
                      maxHeight: 48 * 4.5,
                      width: '240px',
                      background: 'transparent !important',
                      boxShadow: 'none !important',
                      overflow: 'visible',
                    },
                  },
                }}
                anchorOrigin={{ vertical: 'top', horizontal: -20 }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              >
                {monitoringType === 'Limited' && (
                  <>
                    {limitedReportId && onOpenExistingLimitedReport && (
                      <Tooltip title={'Open existing limited report'} arrow={false}>
                        <MenuItem onClick={onOpenExistingLimitedReport}>OPEN LIMITED REPORT</MenuItem>
                      </Tooltip>
                    )}
                    <Tooltip title={'Create a new limited report (will archive current one)'} arrow={false}>
                      <MenuItem onClick={onCreateNewLimitedReport}>CREATE NEW REPORT</MenuItem>
                    </Tooltip>
                  </>
                )}
                {onAddDraw && (
                  <Tooltip title={'Add a new G702/3 to this project'} arrow={false}>
                    <MenuItem
                      onClick={() => {
                        handleClose();
                        onAddDraw();
                      }}
                    >
                      ADD G702/3
                    </MenuItem>
                  </Tooltip>
                )}
                <Tooltip title={'Add comparables and analyze budget'} arrow={false}>
                  <MenuItem onClick={onGenerateReport} disabled={isGeneratingReport}>
                    {isGeneratingReport ? <CircularProgress size={20} sx={{ marginRight: 1 }} /> : null}
                    GENERATE COST ANALYSIS
                  </MenuItem>
                </Tooltip>
                <Tooltip title={'Export project data to Excel'} arrow={false}>
                  <MenuItem onClick={exportProject} disabled={isExporting}>
                    {isExporting ? <CircularProgress size={20} sx={{ marginRight: 1 }} /> : null}
                    EXPORT PROJECT
                  </MenuItem>
                </Tooltip>
                <PermissionGate actionRole='DELETE_PROJECT'>
                  <Tooltip title={'Remove project from CostData. This cannot be undone.'} arrow={false}>
                    <MenuItem onClick={onDelete} disabled={deleting}>
                      {deleting ? <CircularProgress size={20} sx={{ marginRight: 1 }} /> : null}
                      DELETE PROJECT
                    </MenuItem>
                  </Tooltip>
                </PermissionGate>
              </StyledMenu>
            </>
          )}
          <Button
            variant='contained'
            color='primary'
            onClick={onSave}
            startIcon={isSaving ? <CircularProgress size={24} color='inherit' /> : <SaveIcon />}
            disabled={isSaving || !isProjectDirty}
          >
            Save
          </Button>
        </Stack>
      </Stack>
      <UploadDialog
        open={payappUploadDialogOpen}
        onClose={handleClosePayappUploadDialog}
        filesToAccept={{ 'application/pdf': ['.pdf'] }}
        projectId={payappUploadProject?.id}
        projectNumber={payappUploadProject?.projectNumber}
        projectName={payappUploadProject?.projectName}
        initialDraws={payappUploadDraws}
        hideTabs={true}
        defaultTab={1}
      />
      {showMagicReport && (
        <MagicReportDrawSelectDialog
          open={magicReportDrawSelectOpen}
          onClose={closeMagicReportDrawSelect}
          draws={magicReportDraws}
          projectName={magicReportProject?.projectName}
          onConfirm={handleMagicReportDrawConfirm}
          isLoading={magicReportOpening}
          initiallySelectedDrawIds={magicReportExistingDrawIds}
          initiallyHiddenCoLienDrawIds={magicReportExistingHiddenCoLienDrawIds}
          hasExistingReport={hasExistingMagicReport}
          mode={magicReportMode}
        />
      )}
      {showMagicReport && requiredFieldsGapProjectId && requiredFieldsGapProjectSummary && (
        <RequiredFieldsGateDialog
          open={requiredFieldsGapOpen}
          projectId={requiredFieldsGapProjectId}
          missingFields={requiredFieldsGap}
          projectSummary={requiredFieldsGapProjectSummary}
          onSaved={retryAfterFieldsFixed}
          onCancel={closeRequiredFieldsGap}
        />
      )}
    </Footer>
  );
};

export default ProjectFooter;
